import { useEffect } from "react";
import { toast } from "sonner";
import { useDeleteEvent } from "../../../hooks/track/useDeleteEvent";
import { useAnalytics } from "../../../lib/analytics";
import { useClientTranslations } from "../../../lib/i18n/client";
import { TRACK_EVENT_DELETED } from "../../../lib/track/analyticsEvents";
import { parseSyntheticEventId } from "../../../lib/track/parseSyntheticEventId";
import type { Event } from "../../../types/event";
import styles from "./DeleteEventConfirm.module.css";
import { RecurringSeriesBanner } from "./RecurringSeriesBanner";

interface DeleteEventConfirmProps {
	isOpen: boolean;
	event: Event;
	onCancel: () => void;
	/** Called after deleteEvent mutation success — closes both AlertDialog AND parent EventDetailSheet. */
	onSuccess: () => void;
}

const TITLE_ID = "delete-event-confirm-title";

/**
 * Phase 37 D-20 — centred AlertDialog with destructive Confirm.
 *
 * Mirrors DeleteAccountModal STRUCTURE only — destructive button uses a CSS Module
 * class (NOT an inline style attribute setting backgroundColor #dc2626 — pre-existing
 * anti-pattern flagged at PATTERNS.md §"Anti-Patterns Flagged" + RESEARCH §Anti-Pattern,
 * lines 142 + 152 of DeleteAccountModal.tsx).
 *
 * Whole-series banner renders inside the body when parseSyntheticEventId(event.id) !== null
 * (D-19). The banner's `titleInterpolation` flows event.title into user-facing modal copy
 * — the ONLY permitted PHI surface in Phase 37 (RESEARCH §Cross-cutting PHI invariant 4 —
 * gated to user-facing modal copy only; Sentry/PostHog redact `title` via phiScrub.ts).
 *
 * Pitfall 2: `baseId = parseSyntheticEventId(event.id)?.baseId ?? event.id` BEFORE
 * `deleteMutation.mutate(...)` — era-api 400 WHOLE_SERIES_ONLY rejects synthetic ids
 * at the boundary as defence-in-depth.
 *
 * PHI gate (T-37-PHI-ANALYTICS): logEvent payload is enums-only `{ eventType, isRecurring }`.
 * Toast args are static i18n keys only — no `event.title`/`event.location`/etc. interpolation.
 */
export function DeleteEventConfirm({
	isOpen,
	event,
	onCancel,
	onSuccess,
}: DeleteEventConfirmProps) {
	const { t, direction } = useClientTranslations("track");
	const { logTrack } = useAnalytics();
	const deleteMutation = useDeleteEvent();
	const isRecurring = parseSyntheticEventId(event.id) !== null;

	// ESC closes the dialog (call onCancel). Basic focus management.
	// WR-09: do NOT manipulate document.body.style.overflow here — the host
	// BottomSheet (EventDetailSheet) already runs its own scroll-lock and
	// stacking a second lock-and-restore corrupts the saved value. The dialog
	// always renders inside an open BottomSheet, so the parent's lock applies.
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onCancel();
		};
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("keydown", onKey);
		};
	}, [isOpen, onCancel]);

	if (!isOpen) return null;

	const handleConfirm = () => {
		const baseId = parseSyntheticEventId(event.id)?.baseId ?? event.id;
		deleteMutation.mutate(baseId, {
			onSuccess: () => {
				logTrack(TRACK_EVENT_DELETED, {
					eventType: event.type,
					isRecurring,
				});
				toast.success(t("event.delete.successToast"));
				onSuccess();
			},
			onError: () => {
				toast.error(t("event.delete.errorToast"));
			},
		});
	};

	return (
		<div className={styles.overlay}>
			{/* Backdrop is mouse-only — keyboard users dismiss via ESC or the
			    Cancel button in the actions row. tabIndex={-1} keeps it out of
			    the tab order; aria-hidden suppresses it from the a11y tree so
			    screen readers don't announce an unlabeled control. */}
			<button
				type="button"
				className={styles.backdrop}
				tabIndex={-1}
				aria-hidden="true"
				onClick={onCancel}
			/>
			<div
				className={styles.card}
				dir={direction}
				role="alertdialog"
				aria-labelledby={TITLE_ID}
				aria-modal="true"
			>
				<h2 id={TITLE_ID} className={styles.title}>
					{t(`event.delete.title.${event.type}`)}
				</h2>
				<div className={styles.body}>
					{isRecurring ? (
						<RecurringSeriesBanner
							type={event.type}
							variant="delete"
							titleInterpolation={event.title}
						/>
					) : (
						<p className={styles.bodyText}>{t("event.delete.body.single")}</p>
					)}
				</div>
				<div className={styles.actions}>
					<button
						type="button"
						className={`${styles.btn} ${styles.cancelBtn}`}
						onClick={onCancel}
						disabled={deleteMutation.isPending}
					>
						{t("event.delete.actions.cancel")}
					</button>
					<button
						type="button"
						className={`${styles.btn} ${styles.confirmBtn}`}
						onClick={handleConfirm}
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending
							? t("event.delete.actions.deleting")
							: t("event.delete.actions.confirm")}
					</button>
				</div>
			</div>
		</div>
	);
}
